import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, Loader2, Volume2, ChevronLeft, ChevronRight,
  Maximize2, Minimize2, Gauge, FileText, Presentation, Share2,
  Eye, EyeOff, HelpCircle, Flag,
} from "lucide-react";
import { PopQuizButton } from "@/components/PopQuizButton";
import { getNarration, audioUrl, createShareLink } from "@/lib/api";
import { AskProfessor } from "@/components/AskProfessor";
import { ReportInaccuracyDialog } from "@/components/ReportInaccuracyDialog";
import { ShareLectureModal } from "@/components/ShareLectureModal";
import { toast } from "sonner";

/**
 * Auto-fitting slide title. Renders at the base size, measures whether the
 * text overflows its 2-line target, and shrinks the font in 2px steps until
 * it fits (never below 16px). Runs on text/slide changes and container resize.
 * This solves long domain terminology like "Etiology of Diastema: An Overview"
 * wrapping awkwardly at large fixed sizes.
 */
const AutoFitTitle = ({ text, baseClass, maxLines = 2 }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to base sizing before measuring
    el.style.fontSize = "";
    // Compute the pixel size Tailwind's base classes resolved to
    const base = parseFloat(getComputedStyle(el).fontSize);
    // Line-height in px (leading-tight ~= 1.25)
    const lineH = parseFloat(getComputedStyle(el).lineHeight) || base * 1.25;
    const targetMaxHeight = lineH * maxLines + 4; // 4px tolerance for subpixel rounding
    let size = base;
    let guard = 20; // safety cap
    while (el.scrollHeight > targetMaxHeight && size > 14 && guard-- > 0) {
      size -= 2;
      el.style.fontSize = `${size}px`;
    }
  }, [text, maxLines]);
  return (
    <h3 ref={ref} className={`${baseClass} font-serif font-semibold leading-tight`}>
      {text}
    </h3>
  );
};

const VOICES = [
  { id: "sage", label: "Sage" },
  { id: "onyx", label: "Onyx" },
  { id: "nova", label: "Nova" },
  { id: "fable", label: "Fable" },
  { id: "shimmer", label: "Shimmer" },
];
const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const LectureView = ({ lecture, readOnly = false }) => {
  const audioRef = useRef(null);
  const [voice, setVoice] = useState("sage");
  const [url, setUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [slide, setSlide] = useState(0);
  const [fs, setFs] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");

  useEffect(() => {
    setUrl(null);
    setPlaying(false);
    setCur(0);
    setSlide(0);
  }, [lecture?.id]);

  // Arrow-key navigation for slides — a lot of users expect it and never notice the buttons.
  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      // Ignore keys while typing in inputs/textareas or the "Ask Professor" chat
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        setSlide((s) => Math.min((lecture.slides || []).length + ((lecture.end_of_chapter_qas || []).length) - 1, s + 1));
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        setSlide((s) => Math.max(0, s - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lecture?.id, lecture?.slides, lecture?.end_of_chapter_qas]);

  const loadAndPlay = async () => {
    if (loadingAudio) return;
    if (url) return toggle();
    setLoadingAudio(true);
    try {
      const res = await getNarration(lecture.id, voice);
      const full = audioUrl(res.url);
      setUrl(full);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.playbackRate = speed;
          audioRef.current.play();
        }
      }, 120);
    } catch (e) {
      toast.error("Could not generate narration. Please try again.");
    } finally {
      setLoadingAudio(false);
    }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play();
    else a.pause();
  };

  const changeVoice = (v) => {
    setVoice(v);
    setUrl(null);
    setPlaying(false);
  };

  const changeSpeed = (s) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  const baseSlides = lecture.slides || [];
  const paras = (lecture.lecture || "").split(/\n\s*\n/).filter(Boolean);
  // Append end-of-chapter Q&A as extra "question slides" that support a reveal-answer toggle.
  // This lets an instructor present the chapter and reveal the answer live during class.
  const qaSlides = (lecture.end_of_chapter_qas || [])
    .filter((q) => q && q.question && q.answer)
    .map((q, i) => ({
      _kind: "qa",
      _qa: q,
      title: `${q.kind || "Question"} #${i + 1}`,
    }));
  const slides = [...baseSlides, ...qaSlides];
  const [revealedQAs, setRevealedQAs] = useState({});
  const toggleAnswer = (idx) => setRevealedQAs((s) => ({ ...s, [idx]: !s[idx] }));

  const share = async () => {
    try {
      const { path } = await createShareLink(lecture.id);
      const link = `${window.location.origin}${path}`;
      setShareLink(link);
      // Best-effort clipboard copy so users can paste even if they skip the modal.
      try { await navigator.clipboard?.writeText(link); } catch { /* non-blocking */ }
      setShareOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create a share link. Please try again.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
            {lecture.book_title} · {lecture.chapters || "Whole book"}
          </span>
          {lecture.edition && (
            <span data-testid="lecture-edition" className="ml-2 inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground align-middle">
              {lecture.edition}
            </span>
          )}
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {lecture.title}
          </h1>
        </div>
        <div className="no-print shrink-0 flex items-center gap-2">
          {!readOnly && <PopQuizButton lecture={lecture} currentSlideIndex={slide} />}
          {!readOnly && (
            <button
              data-testid="report-inaccuracy-btn"
              onClick={() => setReportOpen(true)}
              title="Report inaccuracy — this doesn't match my book"
              className="flex items-center gap-2 rounded-full border border-border px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:text-destructive hover:border-destructive/40 transition"
            >
              <Flag className="h-4 w-4" />
              <span className="hidden sm:inline">Report</span>
            </button>
          )}
          {!readOnly && (
            <button
              data-testid="share-lecture-btn"
              onClick={share}
              className="flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:border-primary/50 transition-colors"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
          )}
        </div>
      </div>
      <ReportInaccuracyDialog open={reportOpen} onClose={() => setReportOpen(false)} lectureId={lecture.id} />
      <ShareLectureModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        lecture={lecture}
        shareLink={shareLink}
        grade={lecture?.last_grade_letter || lecture?.quiz_grade_letter || "A"}
      />

      {/* Audio Narration Deck — hidden in public/read-only mode (requires auth) */}
      {!readOnly && (
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm mb-8">
        <div className="flex items-center gap-2 mb-4 text-muted-foreground">
          <Volume2 className="h-4 w-4 text-accent" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">AI Voice Narration</span>
        </div>

        <audio
          ref={audioRef}
          src={url || undefined}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setCur(e.target.currentTime)}
          onLoadedMetadata={(e) => setDur(e.target.duration)}
          onEnded={() => setPlaying(false)}
        />

        <div className="flex items-center gap-4">
          <button
            data-testid="audio-play-pause-btn"
            onClick={loadAndPlay}
            className="grid place-items-center h-14 w-14 rounded-full bg-primary text-primary-foreground shrink-0 hover:scale-105 active:scale-95 transition-transform"
          >
            {loadingAudio ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : playing ? (
              <Pause className="h-6 w-6" />
            ) : (
              <Play className="h-6 w-6 ml-0.5" />
            )}
          </button>

          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono text-muted-foreground w-10">{fmt(cur)}</span>
              <input
                data-testid="audio-seek"
                type="range"
                min={0}
                max={dur || 0}
                value={cur}
                onChange={(e) => {
                  if (audioRef.current) audioRef.current.currentTime = Number(e.target.value);
                }}
                className="flex-1 accent-accent h-1.5 cursor-pointer"
              />
              <span className="text-xs font-mono text-muted-foreground w-10">{fmt(dur)}</span>
            </div>
            {/* waveform */}
            <div className="mt-3 flex items-end gap-0.5 h-6">
              {Array.from({ length: 48 }).map((_, i) => (
                <span
                  key={i}
                  className="flex-1 rounded-full bg-accent/60"
                  style={{
                    height: playing ? `${20 + Math.abs(Math.sin(i * 0.7 + cur)) * 80}%` : "18%",
                    transition: "height 0.15s ease",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Voice</span>
            <div className="flex gap-1">
              {VOICES.map((v) => (
                <button
                  key={v.id}
                  data-testid={`voice-${v.id}`}
                  onClick={() => changeVoice(v.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    voice === v.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Gauge className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="flex gap-1">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  data-testid={`speed-${s}`}
                  onClick={() => changeSpeed(s)}
                  className={`px-2 py-1 rounded-full text-xs font-mono transition-colors ${
                    speed === s ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Slides */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-3 text-muted-foreground">
            <Presentation className="h-4 w-4 text-accent" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Slides</span>
          </div>
          <div
            className={`rounded-2xl border border-border bg-card shadow-sm overflow-hidden ${
              fs ? "fixed inset-4 z-50 flex flex-col" : ""
            }`}
          >
            {slides.length > 0 && (
              <div className="relative aspect-[16/10] bg-gradient-to-br from-primary to-primary/80 text-primary-foreground overflow-hidden">
                {slides[slide]?._kind !== "qa" && (slides[slide]?.image_path || slides[slide]?.image_b64) && (
                  <>
                    <img
                      data-testid={`slide-image-${slide}`}
                      src={
                        slides[slide].image_path
                          ? `${process.env.REACT_APP_BACKEND_URL}/api/slide-image/${slides[slide].image_path}`
                          : `data:image/png;base64,${slides[slide].image_b64}`
                      }
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-transparent" />
                  </>
                )}
                <div className="relative z-10 p-8 sm:p-12 flex flex-col h-full">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={slide}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.3 }}
                    className="flex-1 flex flex-col"
                  >
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70 flex items-center gap-2">
                      {slides[slide]._kind === "qa" ? (
                        <><HelpCircle className="h-3.5 w-3.5" /> Chapter Question {slide + 1 - baseSlides.length} / {qaSlides.length}</>
                      ) : (
                        <>Slide {slide + 1} / {slides.length}</>
                      )}
                      {slides[slide]._kind === "qa" && slides[slide]._qa.kind && (
                        <span className="rounded-full bg-accent/25 text-accent-foreground px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal">{slides[slide]._qa.kind}</span>
                      )}
                    </span>
                    {slides[slide]._kind === "qa" ? (
                      <>
                        <AutoFitTitle
                          text={slides[slide]._qa.question}
                          baseClass="mt-3 text-2xl sm:text-3xl lg:text-4xl"
                          maxLines={3}
                        />
                        <div className="mt-8">
                          <button
                            data-testid={`reveal-answer-${slide}`}
                            onClick={() => toggleAnswer(slide)}
                            className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
                          >
                            {revealedQAs[slide] ? <><EyeOff className="h-4 w-4" /> Hide answer</> : <><Eye className="h-4 w-4" /> Reveal answer</>}
                          </button>
                        </div>
                        <AnimatePresence>
                          {revealedQAs[slide] && (
                            <motion.div
                              key="ans"
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              transition={{ duration: 0.35 }}
                              data-testid={`reveal-answer-body-${slide}`}
                              className="mt-6 rounded-2xl bg-background/90 text-foreground p-5 sm:p-6 shadow-lg border border-accent/30 overflow-y-auto max-h-[45%]"
                            >
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Model answer</p>
                              <p className="mt-2 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">{slides[slide]._qa.answer}</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <>
                        <AutoFitTitle
                          text={slides[slide].title}
                          baseClass="mt-3 text-2xl sm:text-3xl lg:text-4xl"
                          maxLines={2}
                        />
                        <ul className="mt-6 space-y-3">
                          {(slides[slide].bullets || []).map((b, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.15 + i * 0.08 }}
                              className="flex gap-3 text-sm sm:text-base"
                            >
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                              <span className="opacity-95">{b}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
              <button
                data-testid="slide-prev-btn"
                onClick={() => setSlide((s) => Math.max(0, s - 1))}
                disabled={slide === 0}
                aria-label="Previous slide"
                className="shrink-0 grid place-items-center h-9 w-9 rounded-full border border-border disabled:opacity-40 hover:border-primary/50 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {slides.length <= 15 ? (
                <div className="flex gap-1.5 flex-wrap justify-center min-w-0">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`slide-dot-${i}`}
                      onClick={() => setSlide(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-accent" : "w-2 bg-border"}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex-1 min-w-0 flex items-center justify-center" data-testid="slide-counter">
                  <span className="tabular-nums text-sm font-semibold text-muted-foreground">
                    {slide + 1} <span className="text-xs opacity-60">/ {slides.length}</span>
                  </span>
                </div>
              )}
              <div className="shrink-0 flex gap-2">
                <button
                  onClick={() => setFs((f) => !f)}
                  aria-label={fs ? "Exit fullscreen" : "Fullscreen"}
                  className="grid place-items-center h-9 w-9 rounded-full border border-border hover:border-primary/50 transition-colors"
                >
                  {fs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button
                  data-testid="slide-next-btn"
                  onClick={() => setSlide((s) => Math.min(slides.length - 1, s + 1))}
                  disabled={slide === slides.length - 1}
                  aria-label="Next slide"
                  className="grid place-items-center h-9 w-9 rounded-full border border-border disabled:opacity-40 hover:border-primary/50 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          {slides[slide] && slides[slide]._kind !== "qa" && slides[slide]?.notes && (
            <div className="mt-3 rounded-xl border border-border bg-secondary/50 p-4">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Presenter Note
              </span>
              <p className="mt-1.5 text-sm text-foreground/80">{slides[slide].notes}</p>
            </div>
          )}
        </div>

        {/* Transcript */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-3 text-muted-foreground">
            <FileText className="h-4 w-4 text-accent" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Transcript</span>
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-6 max-h-[560px] overflow-y-auto custom-scroll">
            {paras.map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-foreground/85 mb-4 last:mb-0">
                {p}
              </p>
            ))}
          </div>
        </div>
      </div>

      <AskProfessor lecture={lecture} readOnly={readOnly} />
    </div>
  );
};
